import type { Artist } from "./artist";
import type { AppCategory } from "./Category";
import type { PortfolioDto } from "./Portfolio";
import type { Location, LookupMap } from "./Locations";
import type { AppLocation, InputLocation } from "./AppLocation";
import { ARTWORK_STATUSES } from "../functions/ArtworkStatus";

export type ValuationType = "acquisition" | "insurance" | "fairMarketValue";
export type PresenceFilter = "any" | "with" | "without";

export type SearchData = {
    clientId?: number | null;
    description?: string;
    artistIds?: number[];
    categoryIds?: number[];
    locations?: number[];
    statusIds?: number[] | null;
    minValue?: number | null;
    maxValue?: number | null;
    currency?: string;
    sort?: string;
    //compactView?: boolean;
    //showValues?: boolean;
    portfolioId?: number | null;
    valuationType: ValuationType;
    acquiredFrom?: string | null;
    acquiredTo?: string | null;

    imageStatus?: PresenceFilter;
    acquisitionValueStatus?: PresenceFilter;
    insuranceValueStatus?: PresenceFilter;
    fairMarketValueStatus?: PresenceFilter;
};

export interface CurrentSearchSummary {
    resultCount?: number;

    portfolioName?: string;
    searchText?: string;

    artistNames?: string[];
    categoryNames?: string[];
    locationNames?: string[];
    statusNames?: string[];

    minValue?: number;
    maxValue?: number;
    currency?: string;

    acquiredFrom?: string | null;
    acquiredTo?: string | null;

    compactDescription?: string;

    imageStatus?: PresenceFilter;
    acquisitionValueStatus?: PresenceFilter;
    insuranceValueStatus?: PresenceFilter;
    fairMarketValueStatus?: PresenceFilter;
}

function flattenLocations(nodes: InputLocation[]): InputLocation[] {
    return nodes.flatMap(n => [
        n,
        ...(n.children ? flattenLocations(n.children) : [])
    ]);
}

export function buildCurrentFilterText(
    search: SearchData,
    artists: Artist[],
    categories: AppCategory[],
    locations: InputLocation[] | null,
    portfolios: PortfolioDto[]
): string {
    const parts: string[] = [];

    const summariseList = (names: string[], max = 2): string => {
        const filtered = names.filter(Boolean);
        if (filtered.length <= max) return filtered.join(", ");
        return `${filtered.slice(0, max).join(", ")} +${filtered.length - max}`;
    };

    const formatCurrencyValue = (value: number, currency?: string): string => {
        const symbol =
            currency === "GBP" ? "£" :
                currency === "USD" ? "$" :
                    currency === "EUR" ? "€" :
                        currency ? `${currency} ` :
                            "";

        return `${symbol}${value.toLocaleString()}`;
    };

    const portfolioName =
        search.portfolioId != null
            ? portfolios.find(p => p.portfolioId === search.portfolioId)?.name
            : undefined;

    if (portfolioName) {
        parts.push(`Portfolio: ${portfolioName}`);
    }

    if (search.description?.trim()) {
        parts.push(`Search: "${search.description.trim()}"`);
    }

    if (search.artistIds?.length) {
        const artistNames = search.artistIds
            .map(id => artists.find(a => a.artistId === id)?.artistName)
            .filter((name): name is string => !!name);

        if (artistNames.length) {
            parts.push(`Artist: ${summariseList(artistNames)}`);
        }
    }

    if (search.categoryIds?.length) {
        const categoryNames = search.categoryIds
            .map(id => categories.find(c => c.id === id)?.name)
            .filter((name): name is string => !!name);

        if (categoryNames.length) {
            parts.push(`Category: ${summariseList(categoryNames)}`);
        }
    }

    if (search.statusIds?.length) {
        const statusNames = search.statusIds
            .map(id => ARTWORK_STATUSES.find(s => s.artworkStatusId === id)?.name)
            .filter((name): name is string => !!name);

        if (statusNames.length) {
            parts.push(`Status: ${summariseList(statusNames)}`);
        }
    }

    if (search.locations?.length) {

        const flatLocations = flattenLocations(locations ?? []);

        const locationLookup = new Map<string, string>(
            flatLocations.map(l => [
                String(l.id),
                l.label ?? l.name ?? l.locationName ?? ""
            ])
        );

        const locationNames = search.locations
            .map(id => locationLookup.get(String(id)))
            .filter((name): name is string => Boolean(name));

        if (locationNames.length) {
            parts.push(`Location: ${summariseList(locationNames)}`);
        }
    }

    if (search.minValue != null || search.maxValue != null) {
        if (search.minValue != null && search.maxValue != null) {
            parts.push(
                `Value: ${formatCurrencyValue(search.minValue, search.currency)} – ${formatCurrencyValue(search.maxValue, search.currency)}`
            );
        } else if (search.minValue != null) {
            parts.push(`Value ≥ ${formatCurrencyValue(search.minValue, search.currency)}`);
        } else if (search.maxValue != null) {
            parts.push(`Value ≤ ${formatCurrencyValue(search.maxValue, search.currency)}`);
        }
    }

    if (search.acquiredFrom || search.acquiredTo) {
        if (search.acquiredFrom && search.acquiredTo) {
            parts.push(`Acquired: ${search.acquiredFrom} – ${search.acquiredTo}`);
        } else if (search.acquiredFrom) {
            parts.push(`Acquired after ${search.acquiredFrom}`);
        } else if (search.acquiredTo) {
            parts.push(`Acquired before ${search.acquiredTo}`);
        }
    }

    if (parts.length === 0) {
        return "All artworks";
    }

    return parts.join(" | ");
}

function summariseList(names: string[], max = 2) {
    if (names.length <= max) return names.join(", ");
    return `${names.slice(0, max).join(", ")} +${names.length - max}`;
}
export function buildCurrentSearchSummary(
    search: SearchData,
    artists: Artist[],
    categories: AppCategory[],
    locationLookup: LookupMap,
    portfolios: PortfolioDto[],
    resultCount?: number
): CurrentSearchSummary {
    const artistNames =
        search.artistIds
            ?.map(id => artists.find(a => a.artistId === id)?.artistName)
            .filter((x): x is string => !!x) ?? [];

    const categoryNames =
        search.categoryIds
            ?.map(id => categories.find(c => c.id === id)?.name)
            .filter((x): x is string => !!x) ?? [];

    const locationNames =
        search.locations
            ?.map(id => locationLookup[id])
            .filter((x): x is string => Boolean(x)) ?? [];

    const statusNames =
        search.statusIds
            ?.map(id => ARTWORK_STATUSES.find(s => s.artworkStatusId === id)?.name)
            .filter((x): x is string => !!x) ?? [];

    const portfolioName =
        search.portfolioId != null
            ? portfolios.find(p => p.portfolioId === search.portfolioId)?.name
            : undefined;

    return {
        resultCount,
        portfolioName,
        searchText: search.description,
        artistNames,
        categoryNames,
        locationNames,
        statusNames,
        minValue: search.minValue ?? undefined,
        maxValue: search.maxValue ?? undefined,
        currency: search.currency,
        acquiredFrom: search.acquiredFrom,
        acquiredTo: search.acquiredTo,
        imageStatus: search.imageStatus,
        acquisitionValueStatus: search.acquisitionValueStatus,
        insuranceValueStatus: search.insuranceValueStatus,
        fairMarketValueStatus: search.fairMarketValueStatus,
    };
}